import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertCircle, Sparkles, Home, Scissors, Phone, Mail,
  MapPin, Instagram,
} from "lucide-react";

// Standalone API client per la pagina pubblica: NON usa @/lib/api perché
// non vogliamo che l'interceptor 401 di lì rimandi al login (qui niente auth).
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalCliente() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/r/${encodeURIComponent(token)}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 410) setError("Questo link è scaduto. Chiedine uno nuovo al tuo salone.");
        else if (status === 404) setError("Il report non è più disponibile.");
        else setError("Link non valido o scaduto.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Niente indicizzazione SEO: la pagina è effimera e privata
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Report personalizzato — AI TricoProtocol";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  if (loading) {
    return (
      <Shell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-7 h-7 text-[#C5A059] animate-spin" />
        </div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell>
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="portal-error">
          <div className="w-14 h-14 rounded-full bg-[#FAFAFA] border border-[#E5E5E0] flex items-center justify-center mb-5">
            <AlertCircle className="w-6 h-6 text-[#737373]" />
          </div>
          <h1 className="font-serif-display text-2xl text-[#1A1A1A] mb-2">
            {error ? "Link non disponibile" : "Errore"}
          </h1>
          <p className="text-sm text-[#737373] max-w-sm">{error || "Si è verificato un errore inatteso."}</p>
        </div>
      </Shell>
    );
  }

  const salon = data.salon;
  const hasContacts = salon && (salon.phone || salon.email || salon.address || salon.instagram);
  const ctaPhone = (salon?.phone || "").replace(/\s+/g, "");
  const ctaInstagram = (salon?.instagram || "").replace(/^@/, "");

  return (
    <Shell salon={salon}>
      <article className="max-w-2xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="portal-cliente-page">
        {/* Hero — saluto formale */}
        <div className="mb-12 animate-fade-up">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3 font-medium">
            Protocollo tricologico personalizzato
          </p>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-[#1A1A1A] leading-snug mb-4">
            {data.intro_greeting || `Gentile cliente,`}
          </h1>
          {data.created_at && (
            <p className="text-xs text-[#737373]">
              Analisi tricologica del {formatDate(data.created_at)}
            </p>
          )}
        </div>

        {/* Spiegazione semplice */}
        {data.spiegazione_cliente && (
          <Section title="Cosa è stato osservato">
            <div className="space-y-4 text-[15px] sm:text-base text-[#1A1A1A] leading-[1.85]">
              {data.spiegazione_cliente.split(/\n\n+/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Section>
        )}

        {/* Foto */}
        {data.photos?.length > 0 && (
          <Section title="Documentazione fotografica">
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {data.photos.map((p, i) => (
                <figure key={i} className="rounded-xl overflow-hidden bg-[#FAFAFA] border border-[#E5E5E0]">
                  <img
                    src={`data:image/jpeg;base64,${p.data}`}
                    alt={p.label || "Foto analisi"}
                    className="w-full aspect-square object-cover"
                  />
                  {p.label && (
                    <figcaption className="text-[10px] text-[#737373] text-center py-1.5 uppercase tracking-wider">
                      {p.label}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </Section>
        )}

        {/* Obiettivi */}
        {data.obiettivi?.length > 0 && (
          <Section title="Obiettivi del percorso">
            <ul className="space-y-2.5">
              {data.obiettivi.map((o, i) => (
                <li key={i} className="text-[15px] text-[#1A1A1A] leading-relaxed flex gap-3">
                  <span className="text-[#C5A059] mt-1.5 shrink-0">●</span>
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Routine domiciliare */}
        {data.routine_domiciliare_portale?.steps?.length > 0 && (
          <Section title={data.routine_domiciliare_portale.titolo || "Routine a casa"} icon={Home}>
            <div className="space-y-7">
              {data.routine_domiciliare_portale.steps.map((step, i) => (
                <div key={i} className="border-l-2 border-[#C5A059] pl-5 py-1">
                  <div className="flex items-baseline gap-2.5 mb-1">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold shrink-0">
                      Step {i + 1}
                    </span>
                  </div>
                  <p className="text-[16px] font-medium text-[#1A1A1A] leading-tight">
                    {step.nome}
                  </p>
                  {step.frequenza && (
                    <p className="text-sm text-[#1A1A1A] mt-1.5">
                      <span className="text-[#737373]">Frequenza: </span>
                      <span className="font-medium">{step.frequenza}</span>
                    </p>
                  )}
                  {step.cosa_fare?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-1.5 font-medium">
                        Cosa fare
                      </p>
                      <ul className="space-y-1">
                        {step.cosa_fare.map((cf, j) => (
                          <li key={j} className="text-sm text-[#1A1A1A] leading-relaxed flex gap-2.5">
                            <span className="text-[#C5A059] mt-1.5 text-[6px] shrink-0">●</span>
                            <span>{cf}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {step.perche && (
                    <div className="mt-3 pt-3 border-t border-[#F2EEE7]">
                      <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-1 font-medium">
                        Perché è importante
                      </p>
                      <p className="text-sm text-[#1A1A1A] italic leading-relaxed">
                        {step.perche}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {data.routine_domiciliare_portale.footer && (
              <p className="mt-6 px-4 py-3 rounded-xl bg-[#FAF6EE] text-sm text-[#1A1A1A] leading-relaxed">
                {data.routine_domiciliare_portale.footer}
              </p>
            )}
          </Section>
        )}

        {/* Protocollo salone */}
        {data.routine_salone_portale && (
          <Section
            title={data.routine_salone_portale.titolo || "Il Suo percorso in salone"}
            icon={Scissors}
          >
            {/* Fasi del trattamento (con nome prodotto completo) */}
            {data.routine_salone_portale.fasi?.length > 0 && (
              <div className="mb-7">
                <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-4 font-medium">
                  Fasi del trattamento
                </p>
                <div className="space-y-7">
                  {data.routine_salone_portale.fasi.map((f, i) => (
                    <div key={i} className="border-l-2 border-[#C5A059] pl-5 py-1">
                      <div className="flex items-baseline gap-2.5 mb-1">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold shrink-0">
                          Fase {f.numero || i + 1}
                        </span>
                      </div>
                      <p className="text-[16px] font-medium text-[#1A1A1A] leading-tight">
                        {f.nome_fase}
                      </p>
                      {f.nome_prodotto && (
                        <p className="text-[13px] text-[#737373] mt-1 leading-snug" data-testid={`fase-prodotto-${i}`}>
                          {f.nome_prodotto}
                        </p>
                      )}
                      {f.cosa && (
                        <div className="mt-3">
                          <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-1.5 font-medium">
                            Cosa verrà effettuato
                          </p>
                          <p className="text-sm text-[#1A1A1A] leading-relaxed">
                            {f.cosa}
                          </p>
                        </div>
                      )}
                      {f.perche && (
                        <div className="mt-3 pt-3 border-t border-[#F2EEE7]">
                          <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-1 font-medium">
                            Perché è importante
                          </p>
                          <p className="text-sm text-[#1A1A1A] italic leading-relaxed">
                            {f.perche}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.routine_salone_portale.obiettivo?.length > 0 && (
              <div className="mb-6 mt-2 pt-6 border-t border-[#F2EEE7]">
                <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-2 font-medium">
                  Obiettivo del trattamento
                </p>
                <ul className="space-y-1.5">
                  {data.routine_salone_portale.obiettivo.map((o, i) => (
                    <li key={i} className="text-[15px] text-[#1A1A1A] leading-relaxed flex gap-3">
                      <span className="text-[#C5A059] mt-1.5 text-[6px] shrink-0">●</span>
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-[#F2EEE7]">
              {data.routine_salone_portale.frequenza && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-1 font-medium">
                    Frequenza consigliata
                  </p>
                  <p className="text-[15px] text-[#1A1A1A] font-medium leading-snug">
                    {data.routine_salone_portale.frequenza}
                  </p>
                </div>
              )}
              {data.routine_salone_portale.percorso && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#737373] mb-1 font-medium">
                    Percorso consigliato
                  </p>
                  <p className="text-[15px] text-[#1A1A1A] leading-snug">
                    {data.routine_salone_portale.percorso}
                  </p>
                </div>
              )}
            </div>
            {data.routine_salone_portale.footer && (
              <p className="mt-6 px-4 py-3 rounded-xl bg-[#FAF6EE] text-sm text-[#1A1A1A] leading-relaxed">
                {data.routine_salone_portale.footer}
              </p>
            )}
          </Section>
        )}

        {/* Blocco PRE-CTA — incoraggiamento formale */}
        {data.pre_cta && (
          <div className="mt-12 mb-2 px-5 py-5 rounded-2xl bg-[#1A1A1A] text-white text-center animate-fade-up">
            <p className="text-[15px] leading-relaxed font-light">
              {data.pre_cta}
            </p>
          </div>
        )}

        {/* CTA */}
        {hasContacts && (
          <div className="mt-8 mb-6 animate-fade-up" data-testid="portal-cta-block">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium text-center mb-4">
              Prenoti il Suo trattamento
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              {ctaPhone && (
                <a
                  href={`tel:${ctaPhone}`}
                  data-testid="portal-cta-phone"
                  className="flex-1 h-13 px-5 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium text-[15px] transition-all flex items-center justify-center gap-2.5"
                >
                  <Phone className="w-4 h-4" />
                  Contatti il salone
                </a>
              )}
              {ctaInstagram && (
                <a
                  href={`https://instagram.com/${ctaInstagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="portal-cta-instagram"
                  className="flex-1 h-13 px-5 rounded-xl bg-white border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] font-medium text-[15px] transition-all flex items-center justify-center gap-2.5"
                >
                  <Instagram className="w-4 h-4" />
                  @{ctaInstagram}
                </a>
              )}
            </div>
          </div>
        )}
      </article>
    </Shell>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section className="mb-10 animate-fade-up">
      <h2 className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#737373] font-medium mb-4 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#C5A059]" />}
        {title}
      </h2>
      {children}
    </section>
  );
}

function Shell({ children, salon }) {
  return (
    <div className="min-h-screen bg-white text-[#1A1A1A] antialiased">
      {/* Header */}
      <header className="border-b border-[#E5E5E0]">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          {salon?.logo_base64 ? (
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={`data:image/png;base64,${salon.logo_base64}`}
                alt={salon.name || "Salone"}
                className="h-8 w-auto max-w-[120px] object-contain"
              />
              {salon.name && (
                <span className="font-serif-display text-base text-[#1A1A1A] truncate hidden sm:inline">
                  {salon.name}
                </span>
              )}
            </div>
          ) : salon?.name ? (
            <span className="font-serif-display text-lg text-[#1A1A1A] truncate">
              {salon.name}
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-serif-display text-base text-[#1A1A1A]">
                AI TricoProtocol
              </span>
            </div>
          )}
        </div>
      </header>

      {children}

      {/* Footer */}
      <footer className="border-t border-[#E5E5E0] mt-16">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8">
          {salon && (salon.phone || salon.email || salon.address || salon.instagram) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-xs text-[#737373]">
              {salon.phone && (
                <ContactRow icon={Phone} label={salon.phone} />
              )}
              {salon.email && (
                <ContactRow icon={Mail} label={salon.email} />
              )}
              {salon.address && (
                <ContactRow icon={MapPin} label={salon.address} />
              )}
              {salon.instagram && (
                <ContactRow icon={Instagram} label={salon.instagram} />
              )}
            </div>
          )}
          <p className="text-center text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">
            Powered by AI TricoProtocol™
          </p>
        </div>
      </footer>
    </div>
  );
}

function ContactRow({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
}
